"use client";

import {
    AssistantMessageContent,
    getAssistantErrorText,
} from "@/components/aios-command/chat/message-renderer";
import type { ContentSegment, ParsedTool } from "@/components/aios-command/chat/message-parser";
import { CommandV2StreamErrorBanner } from "@/components/command-team-v2/cards/command-v2-stream-error-banner";
import type { SelectionOption, SelectionRequest } from "@/types/command-selection";

interface AssistantMessageLike {
    content: string;
    streamError?: string;
    contentSegments?: ContentSegment[];
    tools?: ParsedTool[];
    pending?: boolean;
}

interface MessageContentPanelProps extends AssistantMessageLike {
    messageId: string;
    status?: string;
    selectionDisabled?: boolean;
    onPickSelection?: (option: SelectionOption, request: SelectionRequest) => void;
    streamingPhase?: "working" | "responding";
}

function stripErrorSegments(segments?: ContentSegment[]): ContentSegment[] | undefined {
    if (!segments?.length) return segments;
    const filtered = segments.filter((segment) => segment.type !== "error");
    return filtered.length > 0 ? filtered : undefined;
}

export function hasVisibleAssistantChunk(message: AssistantMessageLike): boolean {
    if (message.content.trim()) return true;
    if ((message.tools?.length ?? 0) > 0) return true;
    if (getAssistantErrorText(message.contentSegments, message.streamError)) return true;
    return (message.contentSegments ?? []).some((segment) => {
        if (segment.type === "text") return Boolean(segment.content?.trim());
        if (segment.type === "tool") return Boolean(segment.tool);
        if (segment.type === "selection") return Boolean(segment.selection);
        if (segment.type === "error") return Boolean(segment.content?.trim());
        return false;
    });
}

export function hasAssistantMessageBody(message: AssistantMessageLike): boolean {
    return Boolean(message.pending || hasVisibleAssistantChunk(message));
}

export function MessageContentPanel({
    content,
    streamError,
    contentSegments,
    tools,
    isStreaming,
    messageId,
    status,
    selectionDisabled,
    onPickSelection,
}: MessageContentPanelProps & { isStreaming?: boolean }) {
    if (!hasAssistantMessageBody({ content, streamError, contentSegments, tools, pending: isStreaming })) {
        return null;
    }

    const errorText = getAssistantErrorText(contentSegments, streamError);
    const bodySegments = stripErrorSegments(contentSegments);
    const hasRenderableBody = hasVisibleAssistantChunk({
        content,
        streamError,
        contentSegments: bodySegments,
        tools,
    });

    if (errorText && !hasRenderableBody && !isStreaming) {
        return <CommandV2StreamErrorBanner text={errorText} />;
    }

    return (
        <div className="w-full space-y-3 text-sm leading-relaxed text-foreground/90">
            {errorText ? <CommandV2StreamErrorBanner text={errorText} /> : null}
            {hasRenderableBody || isStreaming ? (
                <>
                    <AssistantMessageContent
                        content={content}
                        contentSegments={bodySegments}
                        tools={tools}
                        isStreaming={isStreaming}
                        messageId={messageId}
                        status={status}
                        selectionDisabled={selectionDisabled}
                        onPickSelection={onPickSelection}
                    />
                </>
            ) : null}
        </div>
    );
}
